import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageShell, RunHistoryList } from '../components';
import { deleteRun, fetchRuns } from '../lib/api';
import { useResearchQuota } from '../context/QuotaContext';
import type { ResearchRunSummary } from '../types';
import { glassStyle } from '../styles';

const HistoryPage = () => {
  const navigate = useNavigate();
  const { refresh: refreshQuota } = useResearchQuota();

  const [items, setItems] = useState<ResearchRunSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    async function loadFirstPage() {
      try {
        const page = await fetchRuns(null);
        if (!active) return;
        setItems(page.items);
        setNextCursor(page.next_cursor);
      } catch (err) {
        console.error('Failed to load research history:', err);
        if (active) setError('Falha ao carregar o histórico de pesquisas.');
      } finally {
        if (active) setIsLoading(false);
      }
    }

    loadFirstPage();
    return () => {
      active = false;
    };
  }, []);

  const handleLoadMore = useCallback(async () => {
    if (!nextCursor) return;
    setIsLoading(true);
    setError(null);
    try {
      const page = await fetchRuns(nextCursor);
      setItems((prev) => [...prev, ...page.items]);
      setNextCursor(page.next_cursor);
    } catch (err) {
      console.error('Failed to load more research history:', err);
      setError('Falha ao carregar mais pesquisas.');
    } finally {
      setIsLoading(false);
    }
  }, [nextCursor]);

  const handleOpen = useCallback(
    (id: string) => {
      setOpeningId(id);
      navigate(`/history/${id}`);
    },
    [navigate]
  );

  const handleConfirmDelete = useCallback(async (id: string) => {
    setError(null);
    try {
      await deleteRun(id);
      setItems((prev) => prev.filter((item) => item.id !== id));
      void refreshQuota();
    } catch (err) {
      console.error('Failed to delete research run:', err);
      setError('Falha ao excluir a pesquisa.');
    } finally {
      setPendingDeleteId(null);
    }
  }, [refreshQuota]);

  const handleCancelDelete = useCallback(() => setPendingDeleteId(null), []);

  return (
    <PageShell>
      <div className="font-['DM_Sans']">
        <h2 className="text-2xl font-semibold text-gray-900">Histórico de pesquisas</h2>
        <p className="mt-1 text-sm text-gray-600">Reabra ou exclua pesquisas realizadas anteriormente.</p>
      </div>

      {error && (
        <div className={`${glassStyle.card} border-[#FE363B]/30 bg-[#FE363B]/10 font-['DM_Sans']`}>
          <p className="text-[#FE363B]">{error}</p>
        </div>
      )}

      <RunHistoryList
        items={items}
        isLoading={isLoading}
        openingId={openingId}
        pendingDeleteId={pendingDeleteId}
        onOpen={handleOpen}
        onRequestDelete={setPendingDeleteId}
        onCancelDelete={handleCancelDelete}
        onConfirmDelete={handleConfirmDelete}
      />

      {nextCursor && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 bg-white/80 hover:bg-gray-50 transition-colors duration-200 disabled:cursor-default disabled:opacity-60 font-['DM_Sans']"
          >
            Carregar mais
          </button>
        </div>
      )}
    </PageShell>
  );
};

export default HistoryPage;
